<template>
    <div class="row">
        <div class="col-12">
            <store-selector
                v-model="storeUrl"
                :stores="user.Stores"
                title="Select store to retrieve POD information"
                include-no-billing
            />
        </div>

        <div v-if="checkingProviders" class="col-12 align-items-center d-flex flex-column justify-content-center p-4">
            <b-spinner variant="primary" class="mr-2" />
            <div class="mt-2">Checking providers</div>
        </div>

        <!-- Show providers available for this store -->
        <template v-if="!checkingProviders">
            <div v-for="provider in providers" :key="`${provider.label}-${provider.key}`" class="col-4">
                <article class="card mb-2 image-resize" style="object-fit: cover">
                    <img :src="provider.image" :alt="provider.label" class="card-img-top" />
                    <div class="card-body">
                        <h4 class="card-title">{{ provider.label }}</h4>
                        <p class="card-text">
                            {{ provider.description }}
                        </p>

                        <button
                            v-if="isManualPod(provider.key)"
                            class="btn btn-primary"
                            @click="podSelected(provider.key, provider.podId)"
                        >
                            Set up manually
                        </button>
                        <button v-else class="btn btn-primary" @click="podSelected(provider.key, provider.podId)">
                            Link
                        </button>
                    </div>
                </article>
            </div>

            <!-- No providers available -->
            <div v-if="!providers.length" class="col-12 p-3 text-center">No POD providers available</div>
        </template>
    </div>
</template>

<script lang="ts" setup>
import { Ref, computed, onBeforeMount, ref, watch } from 'vue';
import store from '@/store';
import { Provider } from '@/utils/PodProvider.enum';
import { getSettings } from '@/api/storesApp/settings';
import { getPodProviders } from '@/api/admin/podProvider';
import StoreSelector from '@/components/StoreSelector/StoreSelector.vue';

const printifyLabel = 'Printify';
const printfulLabel = 'Printful';
const myProductBasesId = 5;
const podProviderThumbnailPath = 'app/images_pod';

const emit = defineEmits(['select']);

const availableProviders: Ref<string[]> = ref([]);
const checkingProviders = ref(false);
const storeUrl = ref('');

const podProviders: Ref<
    {
        podId: number;
        key: string;
        label: string;
        image: string;
        description: string;
    }[]
> = ref([]);

const user = computed(() => store.state.user);

const providers = computed(() => {
    return podProviders.value.filter((p) => availableProviders.value.includes(p.label));
});

const isManualPod = computed(() => {
    return (pod = '') => ![Provider.PRINTFUL, Provider.PRINTIFY].includes(pod as Provider);
});

watch(storeUrl, () => checkProviders());

onBeforeMount(async () => {
    await loadPodProviders();
    await checkProviders();
});

const loadPodProviders = async () => {
    const providers = await getPodProviders();

    podProviders.value = providers.map((provider) => ({
        podId: provider.id,
        key: provider.apiKeyReference,
        label: provider.name,
        image: `${process.env.VUE_APP_CLOUDFRONT_URL}/${podProviderThumbnailPath}/${provider.imageURL}`,
        description: `Select products from ${provider.name} catalog`
    }));
};

const checkProviders = async () => {
    try {
        if (storeUrl.value) {
            checkingProviders.value = true;
            availableProviders.value = [];

            const settings = await getSettings(storeUrl.value);
            if (settings.printful.enabled && settings.printful.apiKey) {
                availableProviders.value.push(printfulLabel);
            }
            if (settings.printify.enabled && settings.printify.apiKey) {
                availableProviders.value.push(printifyLabel);
            }
            podProviders.value
                .filter(
                    (provider) =>
                        provider.label !== printifyLabel &&
                        provider.label !== printfulLabel &&
                        provider.podId !== myProductBasesId
                )
                .forEach((provider) => {
                    availableProviders.value.push(provider.label);
                });
        }
    } finally {
        checkingProviders.value = false;
    }
};

const podSelected = (podKey: string, podId: number) => {
    const pod = !isManualPod.value(podKey as string) ? podKey : '';
    emit('select', {
        pod,
        storeUrl: storeUrl,
        podId: podId
    });
};
</script>

<style lang="scss" scoped>
.image-resize {
    .card-img-top {
        max-height: 150px !important;
    }
}
</style>
